import React, { useState, useEffect } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminColor } from '@/hooks/admin/useAdmin';
import { useCreateColor, useUpdateColor } from '@/hooks/admin/useAdminColors';

interface ColorModalProps {
    isOpen: boolean;
    onClose: () => void;
    color?: AdminColor;
    onSuccess?: () => void;
}

const ColorModal: React.FC<ColorModalProps> = ({
    isOpen,
    onClose,
    color,
    onSuccess
}) => {
    const [formData, setFormData] = useState({
        name: '',
        hex_code: '#000000',
        additional_cost: 0,
        is_active: true
    });

    const createColor = useCreateColor();
    const updateColor = useUpdateColor();

    const isEditing = !!color;
    const isLoading = createColor.isPending || updateColor.isPending;

    useEffect(() => {
        if (color) {
            setFormData({
                name: color.name,
                hex_code: color.hex_code,
                additional_cost: color.additional_cost,
                is_active: color.is_active
            });
        } else {
            setFormData({
                name: '',
                hex_code: '#000000',
                additional_cost: 0,
                is_active: true
            });
        }
    }, [color, isOpen]);

    const handleHexCodeChange = (value: string) => {
        // Ensure the hex code is valid
        if (value.startsWith('#') && value.length <= 7) {
            setFormData(prev => ({ ...prev, hex_code: value }));
        } else if (!value.startsWith('#') && value.length <= 6) {
            setFormData(prev => ({ ...prev, hex_code: `#${value}` }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            alert('El nombre del color es requerido');
            return;
        }

        if (formData.additional_cost < 0) {
            alert('El costo adicional debe ser mayor o igual a cero');
            return;
        }

        try {
            if (isEditing && color) {
                await updateColor.mutateAsync({
                    id: color.id,
                    data: formData
                });
            } else {
                await createColor.mutateAsync(formData);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error saving color:', error);
            alert('Error al guardar el color');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal-content">
                <div className="admin-modal-header">
                    <h2 className="admin-modal-title">
                        {isEditing ? 'Editar Color' : 'Nuevo Color'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="admin-modal-close"
                        disabled={isLoading}
                    >
                        <RiCloseLine />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="admin-modal-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <Input
                                label="Nombre"
                                name="name"
                                value={formData.name}
                                setValue={(value) => setFormData(prev => ({ ...prev, name: value }))}
                                placeholder="Ingrese el nombre del color"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-form-label">Código Hexadecimal</label>
                            <div className="admin-color-input">
                                <input
                                    type="color"
                                    value={formData.hex_code}
                                    onChange={(e) => setFormData(prev => ({ ...prev, hex_code: e.target.value }))}
                                    className="admin-color-picker"
                                />
                                <Input
                                    name="hex_code"
                                    value={formData.hex_code}
                                    setValue={handleHexCodeChange}
                                    placeholder="#000000"
                                    classNames="admin-hex-input"
                                />
                            </div>
                        </div>

                        <div className="admin-form-group">
                            <Input
                                label="Costo Adicional"
                                name="additional_cost"
                                type="number"
                                step="0.01"
                                value={formData.additional_cost.toString()}
                                setValue={(value) => setFormData(prev => ({ ...prev, additional_cost: parseFloat(value) || 0 }))}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="admin-form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                                />
                                <span>Color Activo</span>
                            </label>
                        </div>

                        <div className="admin-form-group admin-form-full">
                            <div className="admin-color-preview">
                                <span>Vista Previa:</span>
                                <div
                                    className="admin-color-sample"
                                    style={{ backgroundColor: formData.hex_code }}
                                />
                                <span>{formData.hex_code}</span>
                            </div>
                        </div>
                    </div>

                    <div className="admin-modal-actions">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={isLoading}
                        >
                            {isLoading ? <Spinner size="sm" /> : null}
                            {isEditing ? 'Actualizar' : 'Crear'} Color
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ColorModal;
